import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from "recharts";
import { Calculator, IndianRupee, ListOrdered, UserRound, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import {
  CompactStatCard,
  DataTable,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import type { IncomeEntryDto } from "@/types/income.dto";
import ExportMenu from "./ExportMenu";
import DateRangeFilter from "./DateRangeFilter";
import { exportToExcel, exportToPdf, reportFileName, type ExportColumn, type ExportOptions } from "./exportReport";

export type IncomeReportKind = "all" | "direct" | "level" | "reward" | "cashback";

type EntriesQuery = (filter: { type: IncomeReportKind; pageSize: number }) => {
  data?: { items: IncomeEntryDto[]; totalItems: number };
  isLoading: boolean;
  isError: boolean;
};

interface IncomeReportProps {
  kind: IncomeReportKind;
  title: string;
  description: string;
  /** Data hook: admin passes useAdminIncomeEntries, a member passes useIncomeEntries. */
  useEntries: EntriesQuery;
  /** Admin view lists who earned each entry; a member only ever sees their own. */
  showEarner?: boolean;
}

const FETCH_LIMIT = 1000;
const NO_SEARCH_KEYS: (keyof IncomeEntryDto)[] = [];

const inr = (value: number) =>
  `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const matchesSearch = (row: IncomeEntryDto, query: string) =>
  [row.earnerUserName, row.earnerEmail, row.earnerMemberCode, row.sourceUserName, row.sourceMemberCode, row.orderNumber]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(query));

const earnerColumn: DataTableColumn<IncomeEntryDto> = {
    id: "earnerUserName",
    header: "Earner",
    sortable: true,
    mobilePriority: 1,
    cell: (row) => (
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{row.earnerUserName || "User"}</p>
        <p className="truncate font-mono text-xs text-muted-foreground">{row.earnerMemberCode || row.earnerEmail || "—"}</p>
      </div>
    ),
  };

const baseColumns: DataTableColumn<IncomeEntryDto>[] = [
  {
    id: "sourceUserName",
    header: "From / Source",
    sortable: true,
    mobilePriority: 2,
    cell: (row) => (
      <div className="min-w-0">
        <p className="truncate text-sm">{row.sourceUserName || "User"}</p>
        <p className="truncate font-mono text-xs text-muted-foreground">
          {row.sourceMemberCode || (row.orderNumber ? `Order #${row.orderNumber}` : "Claim Reward")}
        </p>
      </div>
    ),
  },
  {
    id: "incomeTypeLabel",
    header: "Type",
    sortable: true,
    hideOnMobile: true,
    cell: (row) => (
      <Badge variant="outline" className="text-xs">
        {row.incomeTypeLabel}
        {row.level ? ` · L${row.level}` : ""}
      </Badge>
    ),
  },
  {
    id: "orderBv",
    header: "Order BV",
    sortable: true,
    align: "right",
    hideOnMobile: true,
    cell: (row) => <span className="font-mono text-sm">{Number(row.orderBv).toFixed(2)}</span>,
  },
  {
    id: "amount",
    header: "Income",
    sortable: true,
    align: "right",
    mobileLabel: "Income",
    mobilePriority: 3,
    cell: (row) => <span className="font-bold text-success">{inr(Number(row.amount))}</span>,
  },
  {
    id: "createdAt",
    header: "Date",
    sortable: true,
    hideOnMobile: true,
    className: "text-sm text-muted-foreground",
    cell: (row) => formatDate(row.createdAt),
  },
];

const earnerExportColumns: ExportColumn<IncomeEntryDto>[] = [
  { header: "Earner", value: (r) => r.earnerUserName || "User", width: 24 },
  { header: "Earner ID", value: (r) => r.earnerMemberCode || "", width: 14 },
  { header: "Earner email", value: (r) => r.earnerEmail || "", width: 28 },
];

const baseExportColumns: ExportColumn<IncomeEntryDto>[] = [
  { header: "Source", value: (r) => r.sourceUserName || "User", width: 24 },
  { header: "Source ID", value: (r) => r.sourceMemberCode || "", width: 14 },
  { header: "Order no.", value: (r) => r.orderNumber || "", width: 20 },
  { header: "Type", value: (r) => r.incomeTypeLabel, width: 18 },
  { header: "Level", value: (r) => r.level ?? "", width: 8 },
  { header: "Order BV", value: (r) => Number(r.orderBv), numeric: true, width: 12 },
  { header: "Income (INR)", value: (r) => Number(r.amount), numeric: true, width: 14 },
];

// Reports are exported as a single-hue chart: identity comes from the label, magnitude from length.
const chartConfig = { total: { label: "Income", color: "hsl(var(--primary))" } } satisfies ChartConfig;

const IncomeEntriesReport = ({ kind, title, description, useEntries, showEarner = false }: IncomeReportProps) => {
  const columns = useMemo(() => (showEarner ? [earnerColumn, ...baseColumns] : baseColumns), [showEarner]);
  const exportColumns = useMemo<ExportColumn<IncomeEntryDto>[]>(
    () => [
      { header: "Date", value: (r) => formatDate(r.createdAt), width: 14 },
      ...(showEarner ? earnerExportColumns : []),
      ...baseExportColumns,
    ],
    [showEarner],
  );

  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data, isLoading, isError } = useEntries({ type: kind, pageSize: FETCH_LIMIT });
  const items = data?.items ?? [];

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const start = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const end = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    return items.filter((row) => {
      const time = new Date(row.createdAt).getTime();
      if (start !== null && time < start) return false;
      if (end !== null && time > end) return false;
      return !query || matchesSearch(row, query);
    });
  }, [items, search, from, to]);

  const table = useDataTable<IncomeEntryDto>({ data: filtered, searchKeys: NO_SEARCH_KEYS });

  const totals = useMemo(() => {
    const amount = filtered.reduce((sum, row) => sum + Number(row.amount), 0);
    // Admin: distinct earners. Member: distinct people the income came from.
    const people = new Set(
      filtered.map((row) =>
        showEarner ? row.earnerUserId || row.earnerEmail || row.earnerUserName : row.sourceUserId || row.sourceUserName,
      ),
    ).size;
    return { amount, people, average: filtered.length ? amount / filtered.length : 0 };
  }, [filtered, showEarner]);

  // "All" splits by income type, "Level" splits by level; the others have nothing to split.
  const breakdown = useMemo(() => {
    if (kind !== "all" && kind !== "level") return [];
    const groups = new Map<string, number>();
    filtered.forEach((row) => {
      const label = kind === "level" ? `Level ${row.level ?? "—"}` : row.incomeTypeLabel;
      groups.set(label, (groups.get(label) ?? 0) + Number(row.amount));
    });
    return Array.from(groups, ([label, total]) => ({ label, total })).sort((a, b) =>
      kind === "level" ? a.label.localeCompare(b.label, undefined, { numeric: true }) : b.total - a.total,
    );
  }, [filtered, kind]);

  const hasFilters = Boolean(search.trim() || from || to);
  const truncated = (data?.totalItems ?? 0) > items.length;

  const dateLabel = from || to ? `${from ? formatDate(from) : "Start"} to ${to ? formatDate(to) : "Today"}` : "All dates";
  const exportOptions = (): ExportOptions<IncomeEntryDto> => ({
    title,
    fileName: reportFileName(`${kind}-income-report`),
    subtitle: dateLabel,
    columns: exportColumns,
    rows: filtered,
    summary: [
      { label: "Total records", value: String(filtered.length) },
      { label: "Total income (INR)", value: totals.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
      { label: showEarner ? "Earners" : "From members", value: String(totals.people) },
    ],
  });

  const clearFilters = () => {
    setSearch("");
    setFrom("");
    setTo("");
    table.setPage(1);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-heading text-xl font-bold tracking-tight text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <ExportMenu
          disabled={isLoading || filtered.length === 0}
          onExcel={() => exportToExcel(exportOptions())}
          onPdf={() => exportToPdf(exportOptions())}
        />
      </div>

      <DateRangeFilter
        idPrefix={kind}
        from={from}
        to={to}
        onFromChange={(value) => {
          setFrom(value);
          table.setPage(1);
        }}
        onToChange={(value) => {
          setTo(value);
          table.setPage(1);
        }}
        hasFilters={hasFilters}
        onClear={clearFilters}
      />

      <StatsGrid columns={4}>
        <CompactStatCard title="Total records" value={isLoading ? "…" : filtered.length.toLocaleString("en-IN")} icon={ListOrdered} />
        <CompactStatCard
          title="Total income"
          value={isLoading ? "…" : inr(totals.amount)}
          icon={IndianRupee}
          iconClassName="bg-gradient-primary"
        />
        <CompactStatCard
          title={showEarner ? "Earners" : "From members"}
          value={isLoading ? "…" : totals.people}
          icon={showEarner ? UserRound : Users}
        />
        <CompactStatCard title="Average per entry" value={isLoading ? "…" : inr(totals.average)} icon={Calculator} />
      </StatsGrid>

      {breakdown.length > 1 && (
        <section className="glass-card rounded-xl border border-border p-3">
          <h3 className="mb-2 text-sm font-semibold text-foreground">{kind === "level" ? "Income by level" : "Income by type"}</h3>
          <ChartContainer config={chartConfig} className="aspect-auto w-full" style={{ height: Math.max(120, breakdown.length * 34 + 16) }}>
            <BarChart data={breakdown} layout="vertical" margin={{ top: 4, right: 72, left: 0, bottom: 0 }}>
              <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border/60" />
              <XAxis type="number" hide />
              <YAxis dataKey="label" type="category" tickLine={false} axisLine={false} width={96} fontSize={11} />
              <ChartTooltip
                cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                content={<ChartTooltipContent hideIndicator formatter={(v) => inr(Number(v))} />}
              />
              <Bar dataKey="total" fill="var(--color-total)" radius={[0, 4, 4, 0]} maxBarSize={18} minPointSize={2}>
                <LabelList
                  dataKey="total"
                  position="right"
                  formatter={(v: number) => inr(v)}
                  className="fill-foreground text-[11px] font-medium"
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </section>
      )}

      {truncated && (
        <p className="text-xs text-muted-foreground">
          Showing the latest {items.length.toLocaleString("en-IN")} of {data?.totalItems.toLocaleString("en-IN")} entries.
          Totals and exports cover these entries only.
        </p>
      )}

      {isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : isError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">
          Could not load this report. Refresh the page to try again.
        </div>
      ) : (
        <DataTable
          title={`${title} entries (${filtered.length})`}
          columns={columns}
          data={table.rows}
          getRowId={(row) => String(row.id)}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            table.setPage(1);
          }}
          searchPlaceholder={showEarner ? "Search earner, source, member ID, order…" : "Search member, member ID, order…"}
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          emptyMessage={hasFilters ? "No entries match these filters." : "No entries yet."}
          renderMobileCard={(row) => (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{row.earnerUserName || "User"}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {row.incomeTypeLabel}
                  {row.level ? ` · L${row.level}` : ""} · from {row.sourceUserName || "User"}
                </p>
                <p className="text-[11px] text-muted-foreground">{formatDate(row.createdAt)}</p>
              </div>
              <span className="shrink-0 font-bold text-success">{inr(Number(row.amount))}</span>
            </div>
          )}
        />
      )}
    </div>
  );
};

export default IncomeEntriesReport;
